import React, { useCallback, useEffect, useState } from "react";
import TreeView from "./TreeView";
import Main from "./Main";
import { Container, Row, Col } from "react-bootstrap";
import Loading from "components/Event/Loading"; // Assuming you have a loading component
import ImageValidator from "./ImageValidator";
import axios from "axios";
import { imageFolderDetail } from "../../api";
import { useSelector } from "react-redux";
import ImagePathEncrypted from "./ImagePathEncrypted";

const eachPage = 500;

const Sidebar = () => {
  const { user } = useSelector((state) => state.auth);
  const [page, setPage] = useState(0);
  const [currentUrl, setCurrentUrl] = useState("");
  const [folders, setFolders] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEncryptedModal, setShowEncryptedModal] = useState(false);
  const [loadingFolderData, setLoadingFolderData] = useState(false); // For folder loading
  const [loadingImages, setLoadingImages] = useState(false); // For image loading
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [btnShow, setBtnShow] = useState(true);
  const [btnEncryptedShow, setEncryptedBtnShow] = useState(true);

  useEffect(() => {
    const fetchRootFolders = async () => {
      setLoadingFolderData(true); // Set loading state for folder data
      try {
        const response = await imageFolderDetail();

        const data = response.data;
        setFolders(data.filter((item) => item.isFolder));
      } catch (error) {
        console.error("Error fetching root folders:", error);
      } finally {
        setLoadingFolderData(false); // Reset loading state
      }
    };

    fetchRootFolders();
  }, []);

  const updateFolderChildren = useCallback(
    (folderList, folderUrl, children) => {
      return folderList.map((folder) => {
        if (folder.url === folderUrl) {
          return { ...folder, children };
        } else if (folder.children) {
          return {
            ...folder,
            children: updateFolderChildren(
              folder.children,
              folderUrl,
              children
            ),
          };
        }
        return folder;
      });
    },
    []
  );

  const fetchChildrenForFolder = useCallback(
    async (folderUrl, queryObj = {}) => {
      setLoadingImages(true); // Start loading images
      try {
        const url = new URL(folderUrl);
        const searchParamsObj = new URLSearchParams(url.search);
        searchParamsObj.set("page", 0);
        searchParamsObj.set("eachPage", 500);

        // Combine existing search parameters with the new query object
        const searchParamsObjData = Object.fromEntries(
          searchParamsObj.entries()
        );
        const paramsObj = { ...searchParamsObjData, ...queryObj };
        const searchParams = new URLSearchParams(paramsObj);
        url.search = searchParams;

        // Make the request using Axios
        const response = await axios.get(url.href, {
          headers: {
            Authorization: user.tokens, // Replace with your actual token
          },
        });

        const data = response.data;
        const foldersData = data.filter((item) => item.isFolder);
        const imagesData = data.filter((item) => !item.isFolder);

        if (folderUrl === currentUrl) {
          if (queryObj.filter_text) {
            // Set selected images to filtered images only
            setSelectedImages(imagesData);
            setCurrentUrl(folderUrl);
          } else if (queryObj.filter_text === undefined) {
            setSelectedImages(imagesData);
            //setSelectedImages((prevImages) => [...prevImages, ...imagesData]); // Append images
          } else {
            setSelectedImages((prevImages) => [...prevImages, ...imagesData]); // Append images
          }
        } else {
          setSelectedImages(imagesData); // Reset selected images
          setCurrentUrl(folderUrl);
        }

        setFolders((prevFolders) =>
          updateFolderChildren(prevFolders, folderUrl, foldersData)
        );
      } catch (error) {
        console.error("Error fetching children for folder:", error);
      } finally {
        setLoadingImages(false); // End loading images
      }
    },
    [currentUrl, updateFolderChildren, user.tokens]
  );

  const handleFolderClick = useCallback(
    async (folderUrl, isChild = false) => {
      setPage(0); // Reset the page
      const isCurrentlyExpanded = expanded.includes(folderUrl);

      if (!isCurrentlyExpanded) {
        // If it's a parent folder
        if (!isChild) {
          // Check if the new folder is a different parent folder
          const isDifferentParent = !expanded.some(
            (url) => folderUrl.startsWith(url) || url === folderUrl
          );

          if (isDifferentParent) {
            // Collapse all other parent folders and expand the new one
            setExpanded([folderUrl]);
          } else {
            // It's the same parent folder; expand it without collapsing others
            setExpanded((prev) => [...prev, folderUrl]);
          }
        } else {
          // If it's a child folder
          const parentFolderUrl = folderUrl.split("/").slice(0, -1).join("/"); // Get parent URL

          // Close other child folders under the same parent
          setExpanded((prev) => {
            // Filter out other child folders but keep the parent
            return prev
              .filter(
                (url) =>
                  !url.startsWith(parentFolderUrl) || url === parentFolderUrl
              )
              .concat(folderUrl); // Expand the clicked child folder
          });
        }

        setCurrentUrl(folderUrl); // Set the current URL
        await fetchChildrenForFolder(folderUrl); // Fetch folder contents
      } else {
        // If it's already expanded, close it
        setExpanded((prev) => prev.filter((url) => url !== folderUrl));
        setSelectedImages([]); // Clear images on collapse
      }
      // eslint-disable-next-line no-use-before-define
    },
    [expanded, fetchChildrenForFolder]
  );

  const checkIfChild = useCallback(
    (folderUrl) => {
      return expanded.some((expandedUrl) => folderUrl.startsWith(expandedUrl));
    },
    [expanded]
  );

  const searchHandle = useCallback(
    (e) => {
      const newSearchTerm = e.target.value;

      setSearchTerm(newSearchTerm);

      if (newSearchTerm.length >= 4) {
        fetchChildrenForFolder(currentUrl, {
          page: 0,
          eachPage,
          filter_text: newSearchTerm,
        });
      } else if (newSearchTerm.length === 0) {
        setPage(0);
        fetchChildrenForFolder(currentUrl, { page: 0, eachPage: 500 });
      }
    },
    [currentUrl, fetchChildrenForFolder]
  );

  const loadMoreImages = useCallback(async () => {
    setLoadingMore(true);
    const newPage = page + 1;
    setPage(newPage);

    const queryObj = {
      page: newPage,
      eachPage,
      filter_text: searchTerm,
    };

    await fetchChildrenForFolder(currentUrl, queryObj);
    setLoadingMore(false);
  }, [currentUrl, fetchChildrenForFolder, page, searchTerm]);

  const handleShowModal = useCallback(() => {
    setShowModal(true);
    document.body.classList.add("modal-open");
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    document.body.classList.remove("modal-open");
  }, []);

  const handleMultipleUrl = useCallback(() => { setBtnShow(false) }, []);
  const handleSingleUrl = useCallback(() => { setBtnShow(true) }, []);
  const handleMultipleUrlEncrypted = useCallback(() => { setEncryptedBtnShow(false) }, []);
  const handleSingleUrlEncrypted = useCallback(() => { setEncryptedBtnShow(true) }, []);
  const handleEncryptedPathCloseModal = useCallback(() => { setShowEncryptedModal(true) }, []);
  const handleEncryptedCloseModal = useCallback(() => { setShowEncryptedModal(false) }, []);

  const handleTreeFolderClick = useCallback(
    (folderUrl) => {
      handleFolderClick(folderUrl, checkIfChild(folderUrl));
      setSearchTerm("");
    },

    [checkIfChild, handleFolderClick]
  );

  return (
    <Container fluid>
      <Row>
        <Col md={2} className="sidebar ">
          <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
            <div>
              <button
                className="btn btn-sm btn-primary ml-2"
                onClick={handleShowModal}
              >
                Image Validator
              </button>
            </div>
            <div>
              <button
                className="btn btn-sm btn-primary mr-2"
                onClick={handleEncryptedPathCloseModal}
              >
                Image Path Encrypted
              </button>
            </div>
          </div>

          {/* <Translate /> */}
          {loadingFolderData ? ( // Show loader for folder data
            <div className="d-flex justify-content-center align-items-center h-75">
              <Loading />
            </div>
          ) : (
            <TreeView
              folders={folders}
              onFolderClick={handleTreeFolderClick}
              expanded={expanded}
            />
          )}
        </Col>
        <Col md={10}>
          <Main
            images={selectedImages}
            loading={loadingImages}
            loadMoreImages={loadMoreImages}
            searchHandle={searchHandle}
            searchTerm={searchTerm}
            loadingMore={loadingMore}
            loadingImages={loadingImages}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        </Col>
      </Row>
      <ImageValidator
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        btnShow={btnShow}
        handleSingleUrl={handleSingleUrl}
        handleMultipleUrl={handleMultipleUrl}
      />
      <ImagePathEncrypted
        showEncryptedModal={showEncryptedModal}
        handleMultipleUrlEncrypted={handleMultipleUrlEncrypted}
        handleSingleUrlEncrypted={handleSingleUrlEncrypted}
        btnEncryptedShow={btnEncryptedShow}
        handleClose={handleEncryptedCloseModal}
      />
    </Container>
  );
};

export default Sidebar;
